<template>
  <BaseCard
    fullHeight
    :heading="filters.capitalize($t('global.items.customer', 1))"
  >
    <Flex direction="column">
      <Customer
        withoutLabel
        required
        :error="showError"
        :customer="customer"
        :ctaTextDefault="$t(`global.actions.add_to_${orderKey()}`)"
        :ctaTextCreate="$t(`global.actions.add_to_${orderKey()}_and_create`)"
        requireAddress
        :requireMedicalData="medical"
        :loading="updatingCustomer"
        @submit="updateCustomer"
        @remove="removeCustomer"
        v-test="'issue-invoice-customer'"
      />
      <BaseAlert
        v-if="alertText"
        color="error"
        :text="alertText"
        size="s"
        :primaryAction="customer && t('global.actions.add_missing_info')"
        @primaryAction="handleAddMissingInfo"
        v-test="'issue-invoice-customer-alert'"
      />
      <BaseAlert
        v-else-if="missingMedicalInfo"
        color="warning"
        :text="t('register.invoice.customer_medical_details_required')"
        size="s"
        :primaryAction="customer && t('global.actions.add_missing_medical_info')"
        @primaryAction="handleAddMissingInfo"
        v-test="'issue-invoice-customer-medical-alert'"
      />
      <CreateCustomerModal
        v-if="showMissingInfoModal"
        :modalData="customer"
        requireAddress
        :requireMedicalData="medical"
        @submit="$emit('updated', $event); showMissingInfoModal = false;"
        @close="showMissingInfoModal = false"
        v-test="'issue-invoice-customer-create-modal'"
      />
    </Flex>
  </BaseCard>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Customer from '@/components/customer/index.vue';
import type { Customer as CustomerType } from '@/components/customer/types';
import filters from '@/filters';
import { orderKey } from '@/helpers/compliant-register';
import { modal } from '@/helpers/ui';
import CreateCustomerModal from '@/modals/create-customer/index.vue';
import { segment } from '@/segment';
import { UPDATE_ORDER } from '../graphql';

const { t } = useI18n();

const props = defineProps<{
  customer: CustomerType | null;
  orderId: string;
  medical: boolean;
  showError: boolean;
}>();

const emit = defineEmits<{
  updated: [customer: CustomerType | null];
}>();

const showMissingInfoModal = ref(false);

const hasFullAddress = computed(() => {
  return (
    props.customer?.address && props.customer?.zipcode && props.customer?.city
  );
});

const alertText = computed(() => {
  if (!props.customer) {
    return t('register.invoice.customer_required');
  }
  if (!hasFullAddress.value) {
    return t('register.invoice.customer_details_required');
  }
  return null;
});

const missingMedicalInfo = computed(() => {
  return (
    props.medical &&
    !!props.customer &&
    (!props.customer.dateOfBirth ||
      !props.customer.personalRegistrationNumber ||
      !props.customer.insuranceProvider)
  );
});

const { mutate: updateOrderMutation, loading: updatingCustomer } =
  useMutation(UPDATE_ORDER);

const removeCustomer = () => {
  modal('confirmation', {
    message: t('register.invoice.customer_remove_confirmation'),
  }).then(() => {
    updateCustomer(null);
  });
};

const updateCustomer = (customer: CustomerType | null) => {
  if (customer?.id === props.customer?.id) {
    emit('updated', customer);
    return;
  }

  updateOrderMutation({
    input: {
      id: props.orderId,
      customerId: customer?.id || null,
    },
  }).then(() => {
    emit('updated', customer);
  });
};

const handleAddMissingInfo = () => {
  segment.track('register.sales.issue_invoice.missing_info_alert.clicked');
  showMissingInfoModal.value = true;
};
</script>
